/**
 * API Constants
 * API configuration and endpoint constants for the Uhamiaji SACCOS Management System
 */

// API Base URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// API Timeout (in milliseconds)
export const API_TIMEOUT = 30000;

// CMS API Timeout (in milliseconds)
export const CMS_API_TIMEOUT = 30000;

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh-token',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    ME: '/auth/me',
    CHANGE_PASSWORD: '/auth/change-password',
    UPDATE_PROFILE: '/auth/profile',
  },
  
  // Members
  MEMBERS: {
    BASE: '/members',
    BY_ID: (id) => `/members/${id}`,
    DOCUMENTS: (id) => `/members/${id}/documents`,
    LOANS: (id) => `/members/${id}/loans`,
    SAVINGS: (id) => `/members/${id}/savings`,
    SHARES: (id) => `/members/${id}/shares`,
    STATEMENT: (id) => `/members/${id}/statement`,
    EXPORT: '/members/export',
    IMPORT: '/members/import',
  },
  
  // Loans
  LOANS: {
    BASE: '/loans',
    BY_ID: (id) => `/loans/${id}`,
    APPLICATIONS: '/loans/applications',
    APPROVE: (id) => `/loans/${id}/approve`,
    REJECT: (id) => `/loans/${id}/reject`,
    DISBURSE: (id) => `/loans/${id}/disburse`,
    SCHEDULE: (id) => `/loans/${id}/schedule`,
    STATEMENT: (id) => `/loans/${id}/statement`,
    GUARANTORS: (id) => `/loans/${id}/guarantors`,
    EXPORT: '/loans/export',
  },
  
  // Savings
  SAVINGS: {
    BASE: '/savings',
    BY_ID: (id) => `/savings/${id}`,
    ACCOUNTS: '/savings/accounts',
    DEPOSIT: (id) => `/savings/${id}/deposit`,
    WITHDRAW: (id) => `/savings/${id}/withdraw`,
    TRANSFER: '/savings/transfer',
    STATEMENT: (id) => `/savings/${id}/statement`,
    EXPORT: '/savings/export',
  },
  
  // Shares
  SHARES: {
    BASE: '/shares',
    BY_ID: (id) => `/shares/${id}`,
    PURCHASE: '/shares/purchase',
    DIVIDENDS: '/shares/dividends',
    STATEMENT: (id) => `/shares/${id}/statement`,
    EXPORT: '/shares/export',
  },
  
  // Repayments
  REPAYMENTS: {
    BASE: '/repayments',
    BY_ID: (id) => `/repayments/${id}`,
    BY_LOAN: (loanId) => `/loans/${loanId}/repayments`,
    SCHEDULE: (loanId) => `/loans/${loanId}/repayment-schedule`,
    EXPORT: '/repayments/export',
  },
  
  // Guarantors
  GUARANTORS: {
    BASE: '/guarantors',
    BY_ID: (id) => `/guarantors/${id}`,
    BY_LOAN: (loanId) => `/loans/${loanId}/guarantors`,
    VERIFY: (id) => `/guarantors/${id}/verify`,
  },
  
  // Accounting
  ACCOUNTING: {
    BASE: '/accounting',
    TRANSACTIONS: '/accounting/transactions',
    CHART_OF_ACCOUNTS: '/accounting/chart-of-accounts',
    JOURNAL_ENTRIES: '/accounting/journal-entries',
    LEDGERS: '/accounting/ledgers',
    TRIAL_BALANCE: '/accounting/trial-balance',
    INCOME_STATEMENT: '/accounting/income-statement',
    BALANCE_SHEET: '/accounting/balance-sheet',
    ACCOUNTS: '/accounting/accounts',
    ACCOUNT_BY_ID: (id) => `/accounting/accounts/${id}`,
  },
  
  // Reports
  REPORTS: {
    BASE: '/reports',
    MEMBERS: '/reports/members',
    LOANS: '/reports/loans',
    SAVINGS: '/reports/savings',
    SHARES: '/reports/shares',
    FINANCIAL: '/reports/financial',
    CUSTOM: '/reports/custom',
    EXPORT: '/reports/export',
    SCHEDULE: '/reports/schedule',
  },
  
  // Users
  USERS: {
    BASE: '/users',
    BY_ID: (id) => `/users/${id}`,
    ROLES: '/users/roles',
    PERMISSIONS: '/users/permissions',
    BY_ROLE: (role) => `/users/role/${role}`,
    ACTIVATE: (id) => `/users/${id}/activate`,
    DEACTIVATE: (id) => `/users/${id}/deactivate`,
  },
  
  // Settings
  SETTINGS: {
    BASE: '/settings',
    GENERAL: '/settings/general',
    ORGANIZATION: '/settings/organization',
    FINANCIAL: '/settings/financial',
    LOAN: '/settings/loan',
    SAVINGS: '/settings/savings',
    EMAIL: '/settings/email',
    SMS: '/settings/sms',
    BACKUP: '/settings/backup',
    RESTORE: '/settings/restore',
    AUDIT_LOG: '/settings/audit-log',
  },
  
  // Notifications
  NOTIFICATIONS: {
    BASE: '/notifications',
    BY_ID: (id) => `/notifications/${id}`,
    MARK_READ: '/notifications/mark-read',
    MARK_ALL_READ: '/notifications/mark-all-read',
    SETTINGS: '/notifications/settings',
    SEND_EMAIL: '/notifications/send-email',
    SEND_SMS: '/notifications/send-sms',
  },
  
  // Dashboard
  DASHBOARD: {
    STATS: '/dashboard/stats',
    CHARTS: '/dashboard/charts',
    ACTIVITIES: '/dashboard/activities',
    ALERTS: '/dashboard/alerts',
  },
};

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

// Pagination Defaults
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  MAX_LIMIT: 100,
  OPTIONS: [10, 25, 50, 100],
};

// Request Methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

export default API_ENDPOINTS;
